import { parsePhoneNumberFromString } from 'libphonenumber-js';

export function extractProfileData(res: any): Record<string, any> | null {
  if (!res || typeof res !== 'object') return null;

  const nested = res.data;
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    if (looksLikeProfile(nested)) return { ...nested };
  }

  if (looksLikeProfile(res) && (res.data == null || typeof res.data !== 'object')) {
    return { ...res };
  }

  if (nested && typeof nested === 'object' && !Array.isArray(nested)) return { ...nested };
  return null;
}

function looksLikeProfile(value: any) {
  if (!value || typeof value !== 'object') return false;
  return (
    value.email != null ||
    value.first_name != null ||
    value.last_name != null ||
    value.phone_number != null ||
    value.profile_picture != null ||
    value.profile_photo != null ||
    value.uuid != null ||
    value.id != null
  );
}

export function toText(value: any): string {
  if (value == null) return '';
  return String(value);
}

export function formatOrSplitPhoneNumber(rawPhone: any, rawCountryCode?: any): { country_code: string; phone_number: string } {
  let phone = toText(rawPhone).trim();
  let cc = toText(rawCountryCode).trim();
  if (cc === 'null' || cc === 'undefined' || cc === 'None') cc = '';

  if (cc && !cc.startsWith('+')) {
    cc = `+${cc}`;
  }

  if (cc && phone) {
    const digitsOnlyCc = cc.replace(/^\+/, '');
    const cleanPhoneDigits = phone.replace(/[^\d+]/g, '');
    if (cleanPhoneDigits.startsWith(`+${digitsOnlyCc}`)) {
      phone = cleanPhoneDigits.slice(1 + digitsOnlyCc.length);
    } else if (cleanPhoneDigits.startsWith(digitsOnlyCc) && cleanPhoneDigits.length > digitsOnlyCc.length + 6) {
      phone = cleanPhoneDigits.slice(digitsOnlyCc.length);
    } else {
      phone = cleanPhoneDigits.replace(/^\+/, '');
    }
    return { country_code: cc, phone_number: phone };
  }

  if (phone) {
    const cleanPhone = phone.replace(/[^\d+]/g, '');
    const phoneWithPlus = cleanPhone.startsWith('+') ? cleanPhone : `+${cleanPhone}`;
    const parsed = parsePhoneNumberFromString(phoneWithPlus);
    if (parsed) {
      return {
        country_code: `+${parsed.countryCallingCode}`,
        phone_number: parsed.nationalNumber,
      };
    }
    const digitsOnly = cleanPhone.replace(/\D/g, '');
    if (digitsOnly.length === 10) {
      return {
        country_code: '+91',
        phone_number: digitsOnly,
      };
    }
    return { country_code: cc || '+91', phone_number: digitsOnly };
  }

  return { country_code: cc || '+91', phone_number: phone };
}

export const DEFAULT_AVATAR = '/static/images/dummy-user.webp';

export function getProfilePhotoUrl(input: any): string | null {
  if (input == null || input === '' || input === 0 || input === '0') return null;

  if (typeof input === 'string') {
    const trimmed = input.trim();
    if (
      !trimmed ||
      trimmed === '0' ||
      trimmed === 'null' ||
      trimmed === 'undefined'
    ) {
      return null;
    }
    return trimmed;
  }

  if (typeof input === 'object') {
    // If a user or wrapper object was passed in directly
    const nestedPicture =
      input.profile_picture ??
      input.profile_photo ??
      input.profile_image ??
      input.avatar ??
      input.photo ??
      input.image;

    if (nestedPicture != null && nestedPicture !== input) {
      const nestedUrl = getProfilePhotoUrl(nestedPicture);
      if (nestedUrl) return nestedUrl;
    }

    const candidate =
      input.media_file_url ||
      input.file_url ||
      input.url ||
      input.media_url ||
      input.image_url ||
      input.media_file ||
      input.image ||
      input.photo ||
      input.file ||
      input.media;

    if (typeof candidate === 'string') {
      const trimmed = candidate.trim();
      if (
        trimmed &&
        trimmed !== '0' &&
        trimmed !== 'null' &&
        trimmed !== 'undefined'
      ) {
        return trimmed;
      }
    }
  }

  return null;
}

export function toProfilePicturePayload(picture: any): string | number | undefined {
  if (picture == null || picture === '' || picture === 0) return undefined;
  if (typeof picture === 'object') {
    const id = picture.id ?? picture.media_id;
    return id == null || id === '' ? undefined : id;
  }
  if (typeof picture === 'number') return picture;
  if (typeof picture === 'string') {
    if (/^https?:\/\//i.test(picture) || picture.startsWith('blob:') || picture.startsWith('data:')) {
      return undefined;
    }
    return picture;
  }
  return undefined;
}

export function getProfileDisplayName(user: any): string {
  const name = [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim();
  if (name) return name;
  if (user?.email) return String(user.email);
  return 'Account';
}

export function mapProfileToForm(res: any) {
  const profile = extractProfileData(res);
  if (!profile) return null;

  const picture = profile.profile_picture ?? profile.profile_photo ?? profile.profile_image ?? profile.avatar ?? null;
  const rawPhone = toText(profile.phone_number ?? profile.phone);
  const rawCountryCode = toText(profile.country_code);
  const { country_code, phone_number } = formatOrSplitPhoneNumber(rawPhone, rawCountryCode);

  return {
    first_name: toText(profile.first_name ?? profile.firstName),
    last_name: toText(profile.last_name ?? profile.lastName),
    phone_number: phone_number,
    country_code: country_code,
    email: toText(profile.email),
    profile_picture: picture,
  };
}

